import { ZoneMetric } from "./metric"
import { Summary as SummaryImpl } from "@/utils/definition"
import {
  PerformanceProfileSettingsActions,
  PerformanceProfileSettingsYAxis,
} from "@/app/report/common/type.ts"
import { MetricThreshold } from "@/types/threshold"

export enum ReportStatus {
  Analyzed = "ANALYZED",
  Analyzing = "ANALYZING",
  UnAnalyzed = "UN_ANALYZED",
}

export interface ReportType {
  idfActivity: number
  idfTeam: number
  idfPlayer: number
  idfSession?: number
  idfData: number
  name: string
  position: string
  backNumber: number
  period: string
  time: string
  idfTeamPlayerGroup?: number
  playerCount?: number
  participants: PlayerSummary[]
  summary: Summary | SummaryImpl
  activityName: string
}

type PlayerSummary = {
  idfPlayer: number
  playerName: string
  playerNumber: number
  rpe: number
}

export type ReportKey = keyof ReportType

export type Report = { [key in ReportKey]: ReportType[key] }

type SessionPlayerSummary = {
  sessions: number[]
  players?: number[]
}

type ChartSessionPlayerSummary = {
  idfSession: number
  playerCount: number
  sessionName: string
  sessionType: string
  dateStart: string
  dateFinish: string
  total: Summary | SummaryImpl
  average: Summary | SummaryImpl
}

type ReadSessionPlayerSummaryParams = SessionPlayerSummary
// type ReadSessionPlayerSummaryResponse = SessionPlayerSummary[]
type ReadSessionPlayerSummaryResponse = ChartSessionPlayerSummary[]
type ReadPlayerSessionSummaryParams = SessionPlayerSummary

type PlayerActivitySummary = {
  activities: number
  players?: number[]
}

type ReadPlayerActivitySummaryParams = PlayerActivitySummary

interface SummaryGeneralType {
  duration: number | null
  totalDistance: number | null
  maxTotalDistance: number | null
  minTotalDistance: number | null
  avgTotalDistance: number | null
  distancePerMins: number | null
  maxSpeed: number | null
  minOfMaxSpeed: number | null
  maxOfMaxSpeed: number | null
  avgOfMaxSpeed: number | null
  maxAcceleration: number | null
  maxDeceleration: number | null
  workload: number | null
  power: number | null
  maxPower: number | null
  minPower: number | null
  avgPower: number | null
  rpe: number | null
  speedZoneDistances: ZoneMetric[]
  speedZoneDistancePerMins: ZoneMetric[]
  speedZoneDurations: ZoneMetric[]
  speedZoneEntries: ZoneMetric[]
}
type SummaryGeneralKey = keyof SummaryGeneralType

type SummaryGeneral = { [key in SummaryGeneralKey]: SummaryGeneralType[SummaryGeneralKey] }

type SummaryActionType = {
  distance: number | null
  maxSpeed: number | null
  maxAcceleration: number | null
  maxDeceleration: number | null
  power: number | null
  maxPower: number | null
  sprintCount: number | null
  hsrCount: number | null
  accelerationCount: number | null
  maxAccelerationCount: number | null
  minAccelerationCount: number | null
  avgAccelerationCount: number | null
  decelerationCount: number | null
  maxDecelerationCount: number | null
  minDecelerationCount: number | null
  avgDecelerationCount: number | null
  sprintDistance: number | null
  hsrDistance: number | null
  maxHsrDistance: number | null
  accelerationDistance: number | null
  decelerationDistance: number | null
  minSprintDistance: number | null
  maxSprintDistance: number | null
  avgSprintDistance: number | null
  maxSprintPower: number | null
  maxHsrPower: number | null
  maxAccelerationPower: number | null
  maxOfMaxAccelerationPower: number | null
  minOfMaxAccelerationPower: number | null
  avgOfMaxAccelerationPower: number | null
  maxDecelerationPower: number | null
  maxOfMaxDecelerationPower: number | null
  minOfMaxDecelerationPower: number | null
  avgOfMaxDecelerationPower: number | null
  accelZoneDistances: ZoneMetric[]
  accelZoneDurations: ZoneMetric[]
  accelZoneEntries: ZoneMetric[]
  decelZoneDistances: ZoneMetric[]
  decelZoneDurations: ZoneMetric[]
  decelZoneEntries: ZoneMetric[]
}
type SummaryActionKey = keyof SummaryActionType

type SummaryAction = { [key in SummaryActionKey]: SummaryActionType[SummaryActionKey] }

type SummaryHrType = {
  avgHr: number | null
  maxHr: number | null
  hrCount: number | null
  hrZoneDistances: ZoneMetric[]
  hrZoneDurations: ZoneMetric[]
  hrZoneDurationPers: ZoneMetric[]
  hrZoneEntries: ZoneMetric[]
}
type SummaryHrKey = keyof SummaryHrType

type SummaryHr = { [key in SummaryHrKey]: SummaryHrType[SummaryHrKey] }

type SummaryActionMetricType = {
  actionCode: string
  count: number
  distance: number
  maxPower: number
  maxDistance: number
}

type RelativeSummaryType = {
  maxSpeedPercent?: number
  maxAccelerationPercent?: number
  maxDecelerationPercent?: number
  maxHrPercent?: number
  speedZoneDistances?: number[]
  speedZoneDistancePerMins?: number[]
  speedZoneDurations?: number[]
  speedZoneEntries?: number[]
  accelZoneDistances?: number[]
  accelZoneDurations?: number[]
  accelZoneEntries?: number[]
  decelZoneDistances?: number[]
  decelZoneDurations?: number[]
  decelZoneEntries?: number[]
  hrZoneDistances?: number[]
  hrZoneDurations?: number[]
  hrZoneDurationPers?: number[]
  hrZoneEntries?: number[]
  runEffort?: SummaryActionMetricType[]
  accelEffort?: SummaryActionMetricType[]
  decelEffort?: SummaryActionMetricType[]
}

type RelativeSummaryKey = keyof RelativeSummaryType

type RelativeSummary = { [key in RelativeSummaryKey]: RelativeSummaryType[key] }

type SummaryType = {
  general: SummaryGeneral
  action: SummaryAction
  hr?: SummaryHr
  playerSummary?: RelativeSummary
  customPlayerSummary?: RelativeSummary
}

type SummaryKey = keyof SummaryType

type Summary = { [key in SummaryKey]: SummaryType[SummaryKey] }

type PlayerReportInSessionSummaryType = {
  idfActivity: number
  idfTeam: number
  /** 선수 식별자. 등번호는 중복·미설정(0)이 가능하므로 선수 매칭은 항상 이 값으로 한다. */
  idfPlayer: number
  playerName: string
  backNumber: number
  uniformNumber?: number
  position: string
  summary: Summary | SummaryImpl
}
type PlayerReportInSessionSummaryTypeKey = keyof PlayerReportInSessionSummaryType
type PlayerReportInSessionSummary = {
  [key in PlayerReportInSessionSummaryTypeKey]: PlayerReportInSessionSummaryType[key]
}
type PlayerSessionSummary = {
  average?: Summary | SummaryImpl
  total?: Summary | SummaryImpl
  players: PlayerReportInSessionSummary[]
}

type ReadPlayerSessionSummaryResponse = PlayerSessionSummary
type ReadPlayerActivitySummaryResponse = PlayerSessionSummary

export enum Interval {
  "1m" = "1m",
  "5m" = "5m",
  "15m" = "15m",
}

type IntervalSummaryParams = {
  interval: Interval
  eventIds?: number[]
  players?: number[]
}

type ReadSessionIntervalSummaryParams = IntervalSummaryParams
type ReadActivityIntervalSummaryParams = IntervalSummaryParams

type IntervalSummaryType = {
  time: string
  total: Summary | SummaryImpl
  average: Summary | SummaryImpl
  playerSummary?: PlayerSessionSummary
  customPlayerSummary?: PlayerSessionSummary
}

type IntervalSessionSummaryType = {
  idfSession: number
  sessionName: string
  summaries: IntervalSummaryType[]
}

type SessionIntervalSummary = {
  idfSession: number
  sessionName: string
  summaries: IntervalSummaryType[]
}
type ReadSessionIntervalSummaryResponse = SessionIntervalSummary[]
type ReadActivityIntervalSummaryResponse = SessionIntervalSummary[]

type CSVDataParams = {
  idfActivityList: number[]
  idfSessionList?: number[]
  idfPlayerList: number[]
}
type ReadDailyCSVDataParams = CSVDataParams
type ReadDailyCSVDataResponse = Report[]
type ReadTrendPlayerCSVDataParams = CSVDataParams
type ReadTrendPlayerCSVDataResponse = Report[]

type TrendReport = {
  idfActivityList: number[]
  idfPlayerList?: number[]
}
type ReadTrendPlayerSummaryParams = TrendReport
type ReadTrendPlayerActivitySummaryParams = TrendReport

type TrendPlayerReport = {
  idfPlayer: number
  playerName: string
  position: string
  uniformNumber: number
  numOfActivity: number
  average: Summary | SummaryImpl
  total: Summary | SummaryImpl
}
type ReadTrendPlayerSummaryResponse = TrendPlayerReport[]

type TrendActivityPlayer = {
  idfActivity: number
  activityName: string
  activityType: string
  matchDayType?: string
  date: string
  numOfPlayers: number
  total: Summary | SummaryImpl
  average: Summary | SummaryImpl
}

type ReadTrendPlayerActivitySummaryResponse = TrendActivityPlayer[]

/* Custom Table Group Types */
type TrendGroupSummaryParams = {
  idfActivityList: number[]
  idfPlayerList: number[]
  groupKey1: string
  groupKey2?: string | null
}
type ReadTrendGroupSummaryParams = TrendGroupSummaryParams

// Daily 리포트 세션 모드의 Custom Table — 선택한 세션만 그룹 집계 (#99, Stats 세션 선택 대체)
type DailySessionGroupSummaryParams = {
  idfSessionList: number[]
  idfPlayerList: number[]
  groupKey1: string
  groupKey2?: string | null
}
type ReadDailySessionGroupSummaryParams = DailySessionGroupSummaryParams

// Daily 리포트 액티비티 모드의 Custom Table — 액티비티 전체 기준(세션 크롭 미반영) (#104)
type DailyActivityGroupSummaryParams = {
  idfActivityList: number[]
  idfPlayerList: number[]
  groupKey1: string
  groupKey2?: string | null
}
type ReadDailyActivityGroupSummaryParams = DailyActivityGroupSummaryParams

type TrendGroupSummaryRow = {
  // Activity 관련
  idfActivity?: number
  activityName?: string
  activityType?: string
  matchDay?: string
  seasonWeek?: number
  activityStartDate?: string
  activityFinishDate?: string
  // Session 관련
  idfSession?: number
  sessionName?: string
  sessionType?: string
  sessionStartDate?: string
  sessionFinishDate?: string
  // Player 관련
  idfPlayer?: number
  nickname?: string
  uniformNumber?: number
  position1?: string
  position2?: string
  playerGroup?: string
  // Period 관련
  date?: string
  week?: string
  month?: string
  year?: number
  // Count 관련
  activityCount?: number
  sessionCount?: number
  playerCount?: number
  // Summary 데이터
  average: Summary | SummaryImpl
  total: Summary | SummaryImpl
}

type ReadTrendGroupSummaryResponse = TrendGroupSummaryRow[]

type GraphSettingsYAxis = {
  metric: string
  chartType: string
  color: string
  unit?: string
  manualScaleActive: boolean
  manualScaleValue: number | null
}

// daily
type SummarySettings = {
  title: string
  metric: string
  calcType: string
}

// daily, trend
type GraphSettings = {
  title: string
  calcType: string
  yAxis: GraphSettingsYAxis[]
}

// daily
type SessionDetailsSettings = {
  calcType: string
  yAxis: GraphSettingsYAxis[]
  dataLabel?: string
  decimalPlace?: number
  sortSetting?: {
    key: string
    order: "asc" | "desc"
  }
}

// daily, trend
type StatsSettings = {
  title: string
  fixedColumns: string[]
  calcType: string
  metrics: string[]
  sortSetting?: {
    key: string
    order: "asc" | "desc"
  }
  thresholds?: MetricThreshold[]
}

// daily
type ScatterChartSettings = {
  xAxis: string
  yAxis: string
  color: string
  xAxisManualScaleActive?: boolean
  xAxisManualScaleValue?: number | null
  yAxisManualScaleActive?: boolean
  yAxisManualScaleValue?: number | null
}

export type TacticMapSettings = {
  title: string
  color: string
  defaultMapType: string
  fixedColumns: string[]
  defaultActionType: string
}

type PerformanceProfileSettings = {
  title: string
  color: string
  yAxis: PerformanceProfileSettingsYAxis[]
  actions: PerformanceProfileSettingsActions[]
}

type TypeSettings =
  | SummarySettings
  | GraphSettings
  | SessionDetailsSettings
  | StatsSettings
  | ScatterChartSettings
  | TacticMapSettings
  | PerformanceProfileSettings

type Section = {
  section: string
  active: boolean
  settings: TypeSettings[] | TypeSettings // Stats의 settings는 배열이 아닌 객체
}
type ReportTemplate = {
  idfTemplate: number
  content: Array<Section>
  name: string
  scope: string
  type: string
  dateUpdated: string
  isLastUsed?: boolean
}

type ReportTemplateMetadata = {
  idfTemplate: number
  name: string
  isLastUsed: boolean
}

type ReadReportTemplatesParams = {
  reportType: string
}
type ReadReportTemplatesResponse = ReportTemplateMetadata[]
type UpsertReportTemplatePayload = ReportTemplate
type UpsertReportTemplateResponse = ReportTemplate

type ReadReportTemplateDetailResponse = ReportTemplate

type PlayerActivityActionDetailsParams = {
  activities: number
  players?: number[]
  actionType: string
  isActivity: boolean
}

type PlayerSessionActionDetailsParams = {
  sessions: number[]
  players?: number[]
  actionType: string
  isSession: boolean
}

type ReadPlayerActivityActionDetailsParams = PlayerActivityActionDetailsParams
type ReadPlayerSessionActionDetailsParams = PlayerSessionActionDetailsParams

type PlayerActivityActionZoneParams = {
  activities: number
  players?: number[]
  actionType: string
  mapType: string
  isActivity: boolean
}

type PlayerSessionActionZoneParams = {
  sessions: number[]
  players?: number[]
  actionType: string
  mapType: string
  isSession: boolean
}

type ReadPlayerActivityAction18ZoneParams = PlayerActivityActionZoneParams
type ReadPlayerSessionAction18ZoneParams = PlayerSessionActionZoneParams

type PlayerActivityFormationMapParams = {
  activities: number
  players?: number[]
  mapType: string
  isActivity: boolean
}

type PlayerSessionFormationMapParams = {
  sessions: number[]
  players?: number[]
  mapType: string
  isSession: boolean
}

type ReadPlayerActivityFormationMapParams = PlayerActivityFormationMapParams
type ReadPlayerSessionFormationMapParams = PlayerSessionFormationMapParams

type PlayerActivityLocationZoneParams = {
  activities: number
  players?: number[]
  mapType: string
  isActivity: boolean
}

type PlayerSessionLocationZoneParams = {
  sessions: number[]
  players?: number[]
  mapType: string
  isSession: boolean
}

type ReadPlayerActivityLocation18ZoneParams = PlayerActivityLocationZoneParams
type ReadPlayerSessionLocation18ZoneParams = PlayerSessionLocationZoneParams
type ReadPlayerActivityLocationHeatmapParams = PlayerActivityLocationZoneParams
type ReadPlayerSessionLocationHeatmapParams = PlayerSessionLocationZoneParams

type ActionMetric = {
  duration: number
  distance: number
  maxSpeed: number
  maxAcceleration: number
  maxDeceleration: number
  interval: number
}

type Point = {
  x: number
  y: number
}

type ActionDetails = {
  idfPlayer: number
  uniformNumber: number
  playerName: string
  position: string
  occurrence: number
  metric: ActionMetric
  isValidTrajectory: boolean
  trajectory: Point[]
}

type PlayerActivityActionDetailsSummary = {
  totalMetricAverage: ActionMetric
  actions: ActionDetails[]
  stadiumX: number
  stadiumY: number
}

type SessionActionDetails = {
  sessionName: string
  sessionMetricAverage: ActionMetric
  actions: ActionDetails[]
}

type PlayerSessionActionDetailsSummary = {
  totalMetricAverage: ActionMetric
  sessions: SessionActionDetails[]
  stadiumX: number
  stadiumY: number
}

type ReadPlayerActivityActionDetailsResponse = PlayerActivityActionDetailsSummary
type ReadPlayerSessionActionDetailsResponse = PlayerSessionActionDetailsSummary

type HeatMapList = {
  minute: number
  heatmap: number[][]
}

type PlayerHeatmapSummary = {
  heatmapList: HeatMapList[]
  xgridCount: number
  ygridCount: number
}

type ReadPlayerActivityAction18ZoneResponse = PlayerHeatmapSummary
type ReadPlayerSessionAction18ZoneResponse = PlayerHeatmapSummary
type ReadPlayerActivityLocation18ZoneResponse = PlayerHeatmapSummary
type ReadPlayerSessionLocation18ZoneResponse = PlayerHeatmapSummary
type ReadPlayerActivityLocationHeatmapResponse = PlayerHeatmapSummary
type ReadPlayerSessionLocationHeatmapResponse = PlayerHeatmapSummary

type FormationPoint = {
  idfPlayer: number
  x: number
  y: number
}

type FormationPlayer = {
  idfPlayer: number
  uniformNumber: number
  nickname: string
  position: string
  isStarting: true
}

type Formation = {
  minute: number
  pointList: FormationPoint[]
}

type PlayerFormationMapSummary = {
  stadiumX: number
  stadiumY: number
  formationList: Formation[]
  playerList: FormationPlayer[]
}

type ReadPlayerActivityFormationMapResponse = PlayerFormationMapSummary
type ReadPlayerSessionFormationMapResponse = PlayerFormationMapSummary

type TrajectoryData = {
  value: number
  trajectory: Point[]
}

export type TrajectoryBounds = {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

/* Performance API Types */
interface PlayerPerformanceParams {
  idfActivity: number
  idfPlayer: number
  dataTypes: string[]
}

type ReadPlayerPerformanceParams = PlayerPerformanceParams

interface PlayerPerformanceData {
  totalOccurrence: number
  speed: number[]
  accel: number[]
  hr: number[]
}

type ReadPlayerPerformanceResponse = PlayerPerformanceData

/* Player Activity Actions API Types */
interface PlayerPerformanceActionsParams {
  idfActivity: number
  idfPlayer: number
  actionTypes: string[]
}

type ReadPlayerPerformanceActionsParams = PlayerPerformanceActionsParams

interface PlayerPerformanceAction {
  actionType: string
  startOccurrence: number
  endOccurrence: number
  duration: number
}

interface PlayerPerformanceActions {
  actions: PlayerPerformanceAction[]
}

type ReadPlayerPerformanceActionsResponse = PlayerPerformanceActions

/* Raw Data Export API Types */
type RawDataFile = {
  fileName: string
  url: string
}

type RawDataExportResponse = {
  zipName: string
  files: RawDataFile[]
}

type ReadRawDataPlayersParams = {
  idfActivity: number
  idfPlayers: number[]
}

type ReadRawDataActivityParams = {
  idfActivity: number
}

type ReadRawDataPlayersResponse = RawDataExportResponse
type ReadRawDataActivityResponse = RawDataExportResponse

export type {
  SessionPlayerSummary,
  ReadSessionPlayerSummaryParams,
  ReadSessionPlayerSummaryResponse,
  ReadPlayerSessionSummaryParams,
  PlayerActivitySummary,
  ReadPlayerActivitySummaryParams,
  IntervalSummaryParams,
  SummaryGeneralType,
  SummaryGeneralKey,
  SummaryGeneral,
  SummaryActionType,
  SummaryActionKey,
  SummaryAction,
  SummaryHrType,
  SummaryHrKey,
  SummaryHr,
  SummaryActionMetricType,
  RelativeSummaryType,
  RelativeSummaryKey,
  RelativeSummary,
  SummaryType,
  SummaryKey,
  Summary,
  SummarySettings,
  Section,
  StatsSettings,
  PlayerReportInSessionSummaryType,
  PlayerReportInSessionSummaryTypeKey,
  PlayerReportInSessionSummary,
  PlayerSessionSummary,
  ReadPlayerSessionSummaryResponse,
  ReadPlayerActivitySummaryResponse,
  ReadSessionIntervalSummaryParams,
  ReadActivityIntervalSummaryParams,
  IntervalSummaryType,
  SessionIntervalSummary,
  ReadSessionIntervalSummaryResponse,
  ReadActivityIntervalSummaryResponse,
  CSVDataParams,
  ReadDailyCSVDataParams,
  ReadDailyCSVDataResponse,
  ReadTrendPlayerCSVDataParams,
  ReadTrendPlayerCSVDataResponse,
  TrendReport,
  ReadTrendPlayerSummaryParams,
  TrendPlayerReport,
  ReadTrendPlayerSummaryResponse,
  ReadTrendPlayerActivitySummaryParams,
  TrendActivityPlayer,
  ReadTrendPlayerActivitySummaryResponse,
  ReadReportTemplatesParams,
  ReadReportTemplatesResponse,
  UpsertReportTemplatePayload,
  UpsertReportTemplateResponse,
  ReportTemplate,
  TypeSettings,
  IntervalSessionSummaryType,
  ChartSessionPlayerSummary,
  ReadReportTemplateDetailResponse,
  ReportTemplateMetadata,
  ReadPlayerActivityActionDetailsParams,
  ReadPlayerSessionActionDetailsParams,
  ReadPlayerActivityActionDetailsResponse,
  ReadPlayerSessionActionDetailsResponse,
  TrajectoryData,
  ActionDetails,
  ReadPlayerActivityAction18ZoneParams,
  ReadPlayerSessionAction18ZoneParams,
  ReadPlayerActivityAction18ZoneResponse,
  ReadPlayerSessionAction18ZoneResponse,
  ReadPlayerActivityLocation18ZoneParams,
  ReadPlayerSessionLocation18ZoneParams,
  ReadPlayerActivityLocationHeatmapParams,
  ReadPlayerSessionLocationHeatmapParams,
  ReadPlayerActivityLocation18ZoneResponse,
  ReadPlayerSessionLocation18ZoneResponse,
  ReadPlayerActivityLocationHeatmapResponse,
  ReadPlayerSessionLocationHeatmapResponse,
  PlayerHeatmapSummary,
  FormationPoint,
  FormationPlayer,
  Formation,
  ReadPlayerActivityFormationMapParams,
  ReadPlayerSessionFormationMapParams,
  ReadPlayerActivityFormationMapResponse,
  ReadPlayerSessionFormationMapResponse,
  ReadPlayerPerformanceParams,
  ReadPlayerPerformanceResponse,
  PlayerPerformanceData,
  ReadPlayerPerformanceActionsParams,
  ReadPlayerPerformanceActionsResponse,
  TrendGroupSummaryParams,
  ReadTrendGroupSummaryParams,
  DailySessionGroupSummaryParams,
  ReadDailySessionGroupSummaryParams,
  DailyActivityGroupSummaryParams,
  ReadDailyActivityGroupSummaryParams,
  TrendGroupSummaryRow,
  ReadTrendGroupSummaryResponse,
  RawDataFile,
  RawDataExportResponse,
  ReadRawDataPlayersParams,
  ReadRawDataActivityParams,
  ReadRawDataPlayersResponse,
  ReadRawDataActivityResponse,
}
